import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Device } from '../../../store/reducers/deviceReducers';
import deviceApi from '../../../api/devices';
import { parseServerError } from '../../../utils/helpers/server-errors';

const pad = (value: number) => String(value).padStart(2, '0');

const toDateTimeLocal = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;

const ApplyFormula = ({
  device,
  open,
  onClose,
  onApplied,
}: {
  device: Device;
  open: boolean;
  onClose: () => void;
  onApplied?: () => void;
}) => {
  const [start, setStart] = useState(() => toDateTimeLocal(new Date(2018, 0, 1, 0, 0)));
  const [end, setEnd] = useState(() => toDateTimeLocal(new Date()));

  useEffect(() => {
    if (!open) return;
    setStart(toDateTimeLocal(new Date(2018, 0, 1, 0, 0)));
    setEnd(toDateTimeLocal(new Date()));
  }, [open]);
  const [loading, setLoading] = useState(false);

  const startDate = new Date(start);
  const endDate = new Date(end);
  const rangeInvalid =
    !start ||
    !end ||
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    endDate < startDate;
  const inclusiveEnd = new Date(endDate);
  inclusiveEnd.setSeconds(59, 999);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const apply = async () => {
    setLoading(true);
    try {
      const response = await deviceApi.applyFormula(device.serial, {
        start: startDate.toISOString(),
        end: inclusiveEnd.toISOString(),
      })();
      const updated = response.data?.updated ?? 0;
      const rawReadings = response.data?.raw_readings ?? 0;
      if (rawReadings === 0) {
        toast.info('No liquid level raw readings in that timeframe.', { position: 'top-center' });
      } else if (updated === 0) {
        toast.info(
          `Found ${rawReadings} raw liquid level samples, but none had a matching sensor reading.`,
          { position: 'top-center' },
        );
      } else if (updated === rawReadings) {
        toast.success(`Updated ${updated} liquid level readings.`, { position: 'top-center' });
        onApplied?.();
      } else {
        toast.success(`Updated ${updated} of ${rawReadings} liquid level readings.`, {
          position: 'top-center',
        });
        onApplied?.();
      }
      onClose();
    } catch (error: any) {
      const message = parseServerError(error?.response?.data).friendlyMessage;
      toast.error(message, { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Apply formula to readings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Replace existing liquid level readings in this window with the saved formula applied to
            raw samples. Battery, signal, and every other reading stay unchanged.
          </Typography>
          <Typography variant='body2' fontFamily="'Courier New', monospace">
            Y = X * {device.formula.converter} + {device.formula.constant} -{' '}
            {device.formula.cable_length}
          </Typography>
          <TextField
            fullWidth
            size='small'
            label='Start'
            type='datetime-local'
            value={start}
            onChange={(event) => setStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            size='small'
            label='End'
            type='datetime-local'
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(start && end && endDate < startDate)}
            helperText={
              start && end && endDate < startDate ? 'End must be on or after the start.' : ' '
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={apply} variant='contained' disabled={loading || rangeInvalid}>
          {loading ? 'Applying...' : 'Apply formula'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyFormula;
